import { parseNetworkError } from '@trello/graphql-error-handling';
import {
  Organization,
  Member,
  QuerySalesTaxArgs,
  QueryCertCaptureTokenArgs,
  QuerySalesTaxForSubscriberArgs,
  QueryRenewalPriceQuotesArgs,
  MutationCreateGoldPaidAccountArgs,
  MutationCreateBusinessClassPaidAccountArgs,
  MutationActivateGoldCreditArgs,
  MutationUpdateBusinessClassCreditCardArgs,
  MutationUpdateGoldCreditCardArgs,
  MutationUpdateBusinessClassPaidProductArgs,
  MutationUpdateGoldPaidProductArgs,
  MutationUpdateBusinessClassBillingContactDetailsArgs,
  MutationUpdateGoldBillingContactDetailsArgs,
  MutationUpdateBusinessClassBillingInvoiceDetailsArgs,
  MutationUpdateGoldBillingInvoiceDetailsArgs,
  MutationReactivateBusinessClassPaidAccountArgs,
  MutationReactivateGoldPaidAccountArgs,
  MutationCancelBusinessClassPaidAccountArgs,
  MutationCancelGoldPaidAccountArgs,
} from '../generated';
import {
  prepareDataForApolloCache,
  QueryInfo,
} from '../prepareDataForApolloCache';
import { JSONObject } from '../types';
import { isActive } from '@trello/paid-account';
import { token } from '@trello/session-cookie';
import { SelectionNode, SelectionSetNode, FieldNode } from 'graphql';
import { clientVersion } from '@trello/config';

type PaidAccountType = 'organizations' | 'members';

/**
 * This CustomType resolver is added to Organization and Member in order to get some
 * expanded properties of the paidAccount that are only accessible through the /statement
 * endpoint. PaidAccount is also listed as a valid nestedResource in the restResourceResolver
 * so the way this works is:
 *  - Apollo will run the restResourceResolver for an Organization or Member query
 *  - If paidAccount is in the query, it will then run this resolver, and pass in the
 *    results of the restResourceResolver as the first argument
 *
 * Within this resolver, we'll check if the GraphQL query is requesting any of the
 * expanded properties and if so, send a request to the /statement endpoint to fetch them.
 * If not, we'll just resolve with an empty object. Apollo will merge the results of the two
 * resolvers together automagically into the cache.
 */
export const paidAccountCustomTypeResolverFactory = (
  type: PaidAccountType,
) => async (
  parentObj: Organization | Member,
  args: null,
  context: object,
  info: QueryInfo,
) => {
  // These properties can only be retrieved by hitting the /paidAccount/statement
  // endpoint. So we check the GraphQL AST to see if you are querying for any of them
  // and hit the /statement endpoint if so
  const extraFields = [
    'sTrelloId',
    'companyName',
    'streetAddress1',
    'streetAddress2',
    'city',
    'state',
    'zip',
    'country',
    'statement',
  ];
  const { selections } = (info?.field?.selectionSet ?? {}) as SelectionSetNode;
  const selectedFields: string[] = (selections ?? [])
    .filter(
      (selection: SelectionNode): selection is FieldNode =>
        selection.kind === 'Field',
    )
    .map((selection: FieldNode) => selection.name.value);
  const customFieldsInSelectionSet = selectedFields.reduce(
    (result, fieldName) => (result ? result : extraFields.includes(fieldName)),
    false,
  );

  if (customFieldsInSelectionSet) {
    try {
      const response = await fetch(
        `/1/${type}/${parentObj.id}/paidAccount/statement`,
        {
          headers: {
            'X-Trello-Client-Version': clientVersion,
          },
        },
      );
      if (!response.ok || [401, 404, 449].includes(response.status)) {
        return parentObj.paidAccount;
      }

      const json = await response.json();
      // Normalize the response using prepareDataForApolloCache, but
      // pick out only the custom fields we care about from the response
      // to prevent overwriting existing data.
      const normalized = prepareDataForApolloCache(
        json,
        info,
        parentObj.__typename,
      ) as JSONObject;
      return extraFields.reduce((result, fieldName) => {
        result[fieldName] = normalized[fieldName];
        return result;
      }, {} as JSONObject);
    } catch {
      // If this response failed, just return what we did get
    }
  }

  return parentObj.paidAccount;
};

export const salesTaxResolver = async (
  obj: object,
  args: QuerySalesTaxArgs,
  context: object,
  info: QueryInfo,
) => {
  const queryParams = new URLSearchParams();
  queryParams.set('country', args.country);
  queryParams.set('subtotal', `${args.subtotal}`);
  if (args.postalCode) {
    queryParams.set('postalCode', args.postalCode);
  }
  if (args.taxId) {
    queryParams.set('taxId', args.taxId);
  }

  const response = await fetch(
    `/1/members/me/paidAccount/salesTax?${queryParams.toString()}`,
    {
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const tax = await response.json();
  return prepareDataForApolloCache(tax, info);
};

export const salesTaxForSubscriberResolver = async (
  obj: object,
  args: QuerySalesTaxForSubscriberArgs,
  context: object,
  info: QueryInfo,
) => {
  const taxes = await Promise.all(
    args.subtotals.map(async (subtotal) => {
      const queryParams = new URLSearchParams();
      queryParams.set('taxDate', args.taxDate);
      queryParams.set('subtotal', `${subtotal}`);
      const response = await fetch(
        `/1/${args.accountType}/${
          args.accountId
        }/paidAccount/salesTaxForSubscriber?${queryParams.toString()}`,
        {
          headers: {
            'X-Trello-Client-Version': clientVersion,
          },
        },
      );

      if (!response.ok) {
        throw await parseNetworkError(response);
      }

      return response.json();
    }),
  );
  return prepareDataForApolloCache(taxes, info);
};

export const renewalPriceQuotesResolver = async (
  obj: object,
  args: QueryRenewalPriceQuotesArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(
    `/1/${args.accountType}/${args.accountId}/paidAccount/renewalPriceQuotes`,
    {
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    },
  );

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const quotes = await response.json();
  return prepareDataForApolloCache(quotes, info);
};

/**
 * Poll the entity endpoint requesting the paidAccount information until
 * the products you requested have been added to the `paidAccount.products`
 * property and the `paidAccount.standing` is among the expected account
 * states to confirm that the purchase has been completed.
 *
 * NOTE: Does not necessarily mean the purchase was successful. In certain
 * cases, it is possible to have a rejected payment, and still get a paid
 * account that is immediately marked as overdue -- but it does mean that
 * the purchase process is done
 */
export const confirmPaidAccount = (
  pollUrl: string,
  products: number[],
): Promise<JSONObject> =>
  new Promise((resolve, reject) => {
    const checkPaidAccountStatus = async () => {
      const response = await fetch(pollUrl, {
        headers: {
          'X-Trello-Client-Version': clientVersion,
        },
      });
      if (response.ok) {
        const model = await response.json();
        const purchaseIsComplete =
          Array.isArray(model.paidAccount?.products) &&
          products.every((product) =>
            model.paidAccount.products.includes(product),
          ) &&
          isActive(model.paidAccount);
        if (purchaseIsComplete) {
          return resolve(model);
        }
      }
      setTimeout(checkPaidAccountStatus, 2000);
    };

    checkPaidAccountStatus();
  });

interface CreatePaidAccountArgs {
  postUrl: string;
  pollUrl: string;
  products: number[];
  nonce: string;
  country: string;
  zipCode?: string;
  taxId?: string;
  discountCode?: string;
  freeTrial?: boolean;
  acceptTOS: boolean;
  billingContactFullName: string;
  billingContactEmail: string;
  billingContactLocale: string;
}

/**
 * To create a paid account, you must first POST all the payment
 * details to the paidAccount endpoint under /organizations or /members.
 * This expects you to have already tokenized the credit card via
 * Braintree (you just pass in the nonce).
 *
 * The endpoint will validate all the details, but the mutation will
 * not immediately take effect. Once the POST succeeds, we then poll
 * the /organization or /member until the paidAccount object is updated.
 */
export const createPaidAccount = async (
  args: CreatePaidAccountArgs,
): Promise<JSONObject> => {
  const { postUrl, pollUrl, ...body } = args;
  const postResponse = await fetch(postUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      ...body,
      // Server seems to error & get into an invalid state if
      // the products are sent in as numbers :noidea:
      // Classic implementation converts these to a string before
      // posting them, so we will too
      products: body.products.map((n) => n.toString()),
      token,
    }),
  });

  if (!postResponse.ok) {
    throw await parseNetworkError(postResponse);
  }

  return confirmPaidAccount(pollUrl, body.products);
};

export const createBusinessClassPaidAccount = async (
  obj: object,
  args: MutationCreateBusinessClassPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const businessClassTeam = await createPaidAccount({
    postUrl: `/1/organizations/${args.idOrganization}/paidAccount`,
    pollUrl: `/1/organizations/${args.idOrganization}?paid_account=true`,
    products: args.products,
    nonce: args.nonce,
    country: args.country,
    zipCode: args.zipCode ?? '',
    taxId: args.taxId ?? '',
    freeTrial: args.freeTrial ?? false,
    discountCode: args.discountCode ?? undefined,
    acceptTOS: args.acceptTOS,
    billingContactFullName: args.name,
    billingContactEmail: args.email,
    billingContactLocale: args.locale,
  });

  return prepareDataForApolloCache(businessClassTeam, info);
};

export const createGoldPaidAccount = async (
  obj: object,
  args: MutationCreateGoldPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const goldMember = await createPaidAccount({
    postUrl: `/1/members/${args.idMember}/paidAccount`,
    pollUrl: `/1/members/${args.idMember}?paid_account=true`,
    products: args.products,
    nonce: args.nonce,
    country: args.country,
    zipCode: args.zipCode ?? '',
    taxId: args.taxId ?? '',
    acceptTOS: args.acceptTOS,
    billingContactFullName: args.name,
    billingContactEmail: args.email,
    billingContactLocale: args.locale,
  });

  return prepareDataForApolloCache(goldMember, info);
};

export const activateGoldCredit = async (
  obj: object,
  args: MutationActivateGoldCreditArgs,
  context: object,
  info: QueryInfo,
) => {
  const postResponse = await fetch(
    `/1/members/${args.idMember}/unpaidAccount`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify({
        // Server seems to error & get into an invalid state if
        // the products are sent in as numbers :noidea:
        // Classic implementation converts these to a string before
        // posting them, so we will too
        products: args.products.map((n) => n.toString()),
        token,
      }),
    },
  );

  if (!postResponse.ok) {
    throw await parseNetworkError(postResponse);
  }

  const goldMember = await confirmPaidAccount(
    `/1/members/${args.idMember}?paid_account=true`,
    args.products,
  );

  return prepareDataForApolloCache(goldMember, info);
};

export const reactivateGoldPaidAccount = async (
  obj: object,
  args: MutationReactivateGoldPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/members/${args.idMember}/paidAccount/reactivate`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      products: [args.product.toString()],
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

export const reactivateBusinessClassPaidAccount = async (
  obj: object,
  args: MutationReactivateBusinessClassPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/organizations/${args.idOrganization}/paidAccount/reactivate`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      products: args.products.map((p) => p.toString()),
      nonce: args.nonce,
      country: args.country,
      zipCode: args.zipCode ?? '',
      taxId: args.taxId ?? '',
      discountCode: args.discountCode ?? undefined,
      acceptTOS: args.acceptTOS,
      billingContactFullName: args.name,
      billingContactEmail: args.email,
      billingContactLocale: args.locale,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

/**
 * Update the Credit Card on file for a paid account
 */
export const updateCreditCardFactory = (type: PaidAccountType) => async (
  obj: object,
  args:
    | MutationUpdateBusinessClassCreditCardArgs
    | MutationUpdateGoldCreditCardArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${type}/${args.accountId}/paidAccount/creditCard`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      nonce: args.nonce,
      country: args.country,
      zipCode: args.zipCode,
      taxId: args.taxId,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

/**
 * Update / change product associated with paid account.
 * This mutation can be used to switch a monthly account to an annual
 * account, or to renew a cancelled subscription before it expires.
 */
export const updatePaidProductFactory = (type: PaidAccountType) => async (
  obj: object,
  args:
    | MutationUpdateBusinessClassPaidProductArgs
    | MutationUpdateGoldPaidProductArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${type}/${args.accountId}/paidAccount/products`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      products: args.products.toString(),
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

export const updateBillingContactDetailsFactory = (
  type: PaidAccountType,
) => async (
  obj: object,
  args:
    | MutationUpdateBusinessClassBillingContactDetailsArgs
    | MutationUpdateGoldBillingContactDetailsArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${type}/${args.accountId}/paidAccount/billingContact`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      billingContactFullName: args.contactName,
      billingContactEmail: args.contactEmail,
      billingContactLocale: args.contactLocale,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

export const updateInvoiceDetailsFactory = (type: PaidAccountType) => async (
  obj: object,
  args:
    | MutationUpdateBusinessClassBillingInvoiceDetailsArgs
    | MutationUpdateGoldBillingInvoiceDetailsArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${type}/${args.accountId}/paidAccount/invoice`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      invoiceDetails: args.invoiceDetails,
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

export const certCaptureTokenResolver = async (
  obj: object,
  args: QueryCertCaptureTokenArgs,
  context: object,
  info: QueryInfo,
) => {
  const response = await fetch(
    `/1/${args.accountType}/${args.accountId}/paidAccount/certCapture/token`,
    {
      headers: {
        'X-Trello-Client-Version': clientVersion,
      },
    },
  );

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};

export const cancelPaidAccountFactory = (type: PaidAccountType) => async (
  obj: object,
  args:
    | MutationCancelBusinessClassPaidAccountArgs
    | MutationCancelGoldPaidAccountArgs,
  context: object,
  info: QueryInfo,
) => {
  const url = `/1/${type}/${args.accountId}/paidAccount/cancel`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': clientVersion,
    },
    body: JSON.stringify({
      token,
    }),
  });

  if (!response.ok) {
    throw await parseNetworkError(response);
  }

  const model = await response.json();
  return prepareDataForApolloCache(model, info);
};
